import { apiRedis } from '@/api/index'
import { CUSTOMER_MENU_INFO } from '@/const/ApiRedis'
import Utils from '@/utils/Utils'
import { HOME } from '@/const/path'

export interface CustomerMenuItem {
  id: number | string
  linkLabel: string
  slug: string
  icon?: string
  badge?: string
  children?: CustomerMenuItem[]
}

export const parseMenuPayload = (res: any): any[] => {
  let data = res?.data?.data ?? res?.data
  if (typeof data === 'string') {
    data = Utils.parseJSON(data)
  }
  if (Array.isArray(data)) return data
  if (Array.isArray(data?.body)) return data.body
  if (Array.isArray(res?.data?.body)) return res.data.body
  return []
}

export const mapMenuItem = (raw: any): CustomerMenuItem | null => {
  const id = raw?.id ?? raw?.menuId
  if (id == null) return null

  const linkLabel = String(
    raw?.linkLabel || raw?.label || raw?.name || raw?.title || ''
  ).trim()
  const slug = String(
    raw?.linkTo || raw?.to || raw?.path || raw?.slug || HOME
  ).trim()

  if (!linkLabel && !slug) return null

  const item: CustomerMenuItem = {
    id,
    linkLabel: linkLabel || slug,
    slug: slug || HOME,
  }

  const icon = raw?.icon || raw?.menuIcon
  if (icon) item.icon = String(icon)

  const badge = raw?.badge || raw?.badgeLabel || raw?.tag
  if (badge) item.badge = String(badge)

  return item
}

const normalizeTreeNode = (raw: any): CustomerMenuItem | null => {
  const item = mapMenuItem(raw)
  if (!item) return null

  if (Array.isArray(raw?.children) && raw.children.length) {
    const children = raw.children
      .map(normalizeTreeNode)
      .filter(Boolean) as CustomerMenuItem[]
    if (children.length) item.children = children
  }

  return item
}

export const buildMenuTree = (items: any[]): CustomerMenuItem[] => {
  if (!items.length) return []

  if (items.some((item) => Array.isArray(item?.children) && item.children.length)) {
    return items.map(normalizeTreeNode).filter(Boolean) as CustomerMenuItem[]
  }

  const nodes = new Map<number | string, CustomerMenuItem>()

  items.forEach((raw) => {
    const item = mapMenuItem(raw)
    if (item) nodes.set(item.id, { ...item, children: [] })
  })

  const roots: CustomerMenuItem[] = []

  items.forEach((raw) => {
    const id = raw?.id ?? raw?.menuId
    const item = nodes.get(id)
    if (!item) return

    const parentId = Number(raw?.parent ?? raw?.parent_id ?? raw?.parentId) || 0
    if (parentId === 0) {
      roots.push(item)
      return
    }

    const parent = nodes.get(parentId)
    if (parent?.children) {
      parent.children.push(item)
    } else {
      roots.push(item)
    }
  })

  const trimEmptyChildren = (list: CustomerMenuItem[]) => {
    list.sort((a, b) => Number(a.id) - Number(b.id))
    list.forEach((node) => {
      if (node.children?.length) {
        trimEmptyChildren(node.children)
      } else {
        delete node.children
      }
    })
  }

  trimEmptyChildren(roots)
  return roots
}

export const fetchCustomerMenu = async (): Promise<CustomerMenuItem[]> => {
  try {
    const res: any = await apiRedis(CUSTOMER_MENU_INFO)
    return buildMenuTree(parseMenuPayload(res))
  } catch {
    return []
  }
}
